import { useState, useEffect, useMemo, useCallback } from "react";
import { Form } from "react-admin";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { makeStyles } from "@mui/styles";
import LoadingOverlay from "../../../core/LoadingOverlay";
import ConfirmDialog from "../../../core/ConfirmDialog";
import DismissibleMessage from "../DismissibleMessage";
import { calibrationService } from "../../../../services/CalibrationService";
import { ClientsDropdown, MultiProjectsDropdown } from "../DropdownV2";
import { PHASES, toggleConsecutivePhase, computePhase3DateRange, buildPhaseExtraSummary } from "./phaseSelection";
import { PhaseHeader, StartEndDateFields, ResumeCheckbox } from "./phaseControls";

const DEFAULT_GROUP_KEY = "default";
const CONCURRENT_SUBMISSIONS = 5;

// Project eligibility states
const ELIGIBILITY = {
    CHECKING: "checking",
    ELIGIBLE: "eligible",
    MISSING: "missing",
    ERROR: "error",
};

// Per-run statuses for the post-submit grid
const RUN_STATUS = {
    PENDING: "pending",
    SUBMITTING: "submitting",
    SUBMITTED: "submitted",
    SUBMITTED_TIMEOUT: "submitted_timeout",
    FAILED: "failed",
};

const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    dropdownRow: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        gap: theme.spacing(2),
        margin: "10px 0",
        width: "100%",
        maxWidth: "700px",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    phaseCard: {
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "16px",
        margin: "8px 0",
        width: "100%",
        maxWidth: "700px",
        backgroundColor: "#fafafa",
    },
    tableContainer: {
        width: "100%",
        maxWidth: "900px",
        margin: "16px 0",
    },
    buttonRow: {
        display: "flex",
        justifyContent: "center",
        margin: "20px 0",
    },
}));

async function runWithConcurrency(items, worker, concurrency) {
    const queue = items.slice();
    const runners = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
        while (queue.length > 0) {
            const item = queue.shift();
            await worker(item);
        }
    });
    await Promise.all(runners);
}

export default function BulkKickoff() {
    const classes = useStyles();
    const [clientName, setClientName] = useState(null);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [selectedPhases, setSelectedPhases] = useState([1, 2, 3, 4]);
    // Phase 3 date range — applied to every project in the bulk run
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    // Per-run resume flags, applied to every project. Phase 1 resume disables the
    // backend's default overwrite; Phase 3 resume continues an incomplete run.
    const [phase1Resume, setPhase1Resume] = useState(false);
    const [phase3Resume, setPhase3Resume] = useState(false);
    // Map<projectName, { state: ELIGIBILITY, versionDttm?: string, error?: string }>
    const [eligibility, setEligibility] = useState({});
    // Map<projectName, { state: RUN_STATUS, error?: string }>
    const [runStatus, setRunStatus] = useState({});
    const [errorMessage, setErrorMessage] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const togglePhase = (phaseNumber) => {
        setSelectedPhases((prev) => toggleConsecutivePhase(prev, phaseNumber));
    };

    const firstSelectedPhase = useMemo(() => {
        if (selectedPhases.length === 0) return null;
        return PHASES.find((p) => p.number === Math.min(...selectedPhases));
    }, [selectedPhases]);

    // Phase 3 date validation
    const dateState = computePhase3DateRange(startDate, endDate);
    const { effectiveStart, effectiveEnd, dateRangeError } = dateState;

    useEffect(() => {
        setSelectedProjects([]);
        setEligibility({});
        setRunStatus({});
        setStatusMessage(null);
        setErrorMessage(null);
    }, [clientName]);

    useEffect(() => {
        setRunStatus({});
    }, [selectedProjects, selectedPhases]);

    // Re-run eligibility check when client / projects / first-selected phase change
    const checkEligibility = useCallback(async () => {
        if (!clientName || selectedProjects.length === 0 || !firstSelectedPhase) {
            setEligibility({});
            return;
        }
        const initial = {};
        for (const p of selectedProjects) {
            initial[p] = { state: ELIGIBILITY.CHECKING };
        }
        setEligibility(initial);

        const results = await Promise.allSettled(
            selectedProjects.map((project) =>
                calibrationService.getLatestDatasetViaSignedUrl(
                    clientName,
                    project,
                    DEFAULT_GROUP_KEY,
                    firstSelectedPhase.datasetType,
                    false
                )
            )
        );
        const next = {};
        results.forEach((result, idx) => {
            const project = selectedProjects[idx];
            if (result.status === "fulfilled" && result.value) {
                const versionDttm = result.value?.meta?.version_dttm;
                next[project] = { state: ELIGIBILITY.ELIGIBLE, versionDttm };
            } else if (result.status === "rejected") {
                const status = result.reason?.response?.status;
                if (status === 404) {
                    next[project] = { state: ELIGIBILITY.MISSING };
                } else {
                    next[project] = {
                        state: ELIGIBILITY.ERROR,
                        error: result.reason?.message || String(result.reason),
                    };
                }
            } else {
                next[project] = { state: ELIGIBILITY.MISSING };
            }
        });
        setEligibility(next);
    }, [clientName, selectedProjects, firstSelectedPhase]);

    useEffect(() => {
        checkEligibility();
    }, [checkEligibility]);

    const eligibleProjects = useMemo(
        () => selectedProjects.filter((p) => eligibility[p]?.state === ELIGIBILITY.ELIGIBLE),
        [selectedProjects, eligibility]
    );

    const canSubmit =
        clientName &&
        eligibleProjects.length > 0 &&
        selectedPhases.length > 0 &&
        !submitting &&
        firstSelectedPhase &&
        !dateRangeError;

    const phaseLabel =
        selectedPhases.length === PHASES.length ? "all phases" : `phase(s) ${selectedPhases.join(", ")}`;

    const handleSubmit = async () => {
        setConfirmModalOpen(false);
        setSubmitting(true);
        setErrorMessage(null);
        setStatusMessage(null);

        const initialRunStatus = {};
        eligibleProjects.forEach((p) => {
            initialRunStatus[p] = { state: RUN_STATUS.PENDING };
        });
        setRunStatus(initialRunStatus);

        // Same extra for every project. Skip dates that are today/future — the
        // backend will fall back to its defaults (start = 5 years ago, end = today).
        const extra = { phases: selectedPhases };
        // Backend defaults overwrite=True; Phase 1 "resume" opts out of that.
        if (selectedPhases.includes(1) && phase1Resume) {
            extra.overwrite = false;
        }
        if (selectedPhases.includes(3)) {
            if (effectiveStart) extra.start_date = effectiveStart.toISOString();
            if (effectiveEnd) extra.end_date = effectiveEnd.toISOString();
            if (phase3Resume) extra.resume = true;
        }

        await runWithConcurrency(
            eligibleProjects,
            async (project) => {
                setRunStatus((prev) => ({ ...prev, [project]: { state: RUN_STATUS.SUBMITTING } }));
                // version_dttm: null tells the backend to resolve "latest" itself.
                // Eligibility already proved a latest exists for this project.
                const inputs = [
                    {
                        dataset_type: firstSelectedPhase.datasetType,
                        group_key: DEFAULT_GROUP_KEY,
                        version_dttm: null,
                    },
                ];
                try {
                    await calibrationService.triggerPitPipeline(clientName, project, extra, inputs);
                    setRunStatus((prev) => ({ ...prev, [project]: { state: RUN_STATUS.SUBMITTED } }));
                } catch (err) {
                    const isTimeout = err === "Network Error" || err?.code === "ERR_NETWORK";
                    setRunStatus((prev) => ({
                        ...prev,
                        [project]: {
                            state: isTimeout ? RUN_STATUS.SUBMITTED_TIMEOUT : RUN_STATUS.FAILED,
                            error: isTimeout ? undefined : err?.message || String(err),
                        },
                    }));
                }
            },
            CONCURRENT_SUBMISSIONS
        );

        setStatusMessage(`Submitted ${phaseLabel} for ${eligibleProjects.length} project(s).`);
        setSubmitting(false);
    };

    const extraSummary = buildPhaseExtraSummary({
        selectedPhases,
        phase1Resume,
        phase3Resume,
        startDate,
        endDate,
        effectiveStart,
        effectiveEnd,
    });
    const confirmMessage = [
        `Run ${phaseLabel} for the following ${eligibleProjects.length} project(s) under client "${clientName}"?`,
        ...(extraSummary.length ? ["", ...extraSummary] : []),
        "",
        ...eligibleProjects.map((p) => `• ${p}`),
    ].join("\n");

    const eligibilityChip = (state) => {
        switch (state) {
            case ELIGIBILITY.ELIGIBLE:
                return <Chip label="Eligible" color="success" size="small" />;
            case ELIGIBILITY.MISSING:
                return <Chip label="Missing input" color="default" size="small" />;
            case ELIGIBILITY.ERROR:
                return <Chip label="Error" color="error" size="small" />;
            case ELIGIBILITY.CHECKING:
            default:
                return <Chip label="Checking…" size="small" />;
        }
    };

    const runStatusChip = (entry) => {
        if (!entry) return null;
        switch (entry.state) {
            case RUN_STATUS.SUBMITTED:
                return <Chip label="Submitted" color="primary" size="small" />;
            case RUN_STATUS.SUBMITTED_TIMEOUT:
                return <Chip label="Submitted (timeout — expected)" color="primary" size="small" />;
            case RUN_STATUS.SUBMITTING:
                return <Chip label="Submitting…" size="small" />;
            case RUN_STATUS.FAILED:
                return <Chip label={`Failed: ${entry.error || ""}`} color="error" size="small" />;
            case RUN_STATUS.PENDING:
            default:
                return <Chip label="Pending" size="small" />;
        }
    };

    const progressViewerUrlFor = (project) =>
        `/calibration/progressViewer?client=${encodeURIComponent(clientName || "")}&project=${encodeURIComponent(
            project
        )}&processTypes=TriggerPitPipeline`;

    const hasRunStatus = Object.keys(runStatus).length > 0;

    return (
        <div style={{ paddingBottom: "200px" }}>
            <ConfirmDialog
                open={confirmModalOpen}
                title="Confirm Bulk Historical DLR Pipeline Trigger"
                handleClose={() => setConfirmModalOpen(false)}
                handleConfirm={handleSubmit}
                message={confirmMessage}
            />
            <Form className={classes.formContainer}>
                <DismissibleMessage message={errorMessage} type="error" onDismiss={() => setErrorMessage(null)} />
                <DismissibleMessage message={statusMessage} type="success" onDismiss={() => setStatusMessage(null)} />
                <LoadingOverlay isLoading={submitting} message="Submitting bulk kickoff..." />

                <Typography
                    variant="body2"
                    style={{ textAlign: "center", maxWidth: "700px", margin: "0 auto 16px", color: "#666" }}
                >
                    Pick a client, multi-select its projects, and choose a consecutive phase range. The latest input
                    dataset for the first selected phase is resolved per project; projects missing it are skipped.
                </Typography>

                <div className={classes.dropdownRow}>
                    <ClientsDropdown
                        value={clientName}
                        setValue={setClientName}
                        styleClass={{ flex: 1, minWidth: "150px", maxWidth: "300px" }}
                    />
                    <MultiProjectsDropdown
                        clientName={clientName}
                        value={selectedProjects}
                        setValue={setSelectedProjects}
                        styleClass={{ flex: 2, minWidth: "200px", maxWidth: "400px" }}
                    />
                </div>

                {PHASES.map((phase) => (
                    <div key={phase.number} className={classes.phaseCard}>
                        <PhaseHeader
                            phase={phase}
                            checked={selectedPhases.includes(phase.number)}
                            onToggle={() => togglePhase(phase.number)}
                        />
                        {phase.number === 1 && selectedPhases.includes(1) && (
                            <ResumeCheckbox
                                checked={phase1Resume}
                                onChange={setPhase1Resume}
                                caption="Reuse existing outputs instead of overwriting"
                            />
                        )}
                        {phase.number === 3 && selectedPhases.includes(3) && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                <Typography variant="body2" color="textSecondary">
                                    Optional — applied to every selected project. Leave blank to use the backend
                                    defaults.
                                </Typography>
                                <div
                                    style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: "16px",
                                        alignItems: "center",
                                    }}
                                >
                                    <StartEndDateFields
                                        startDate={startDate}
                                        setStartDate={setStartDate}
                                        endDate={endDate}
                                        setEndDate={setEndDate}
                                        dateState={dateState}
                                    />
                                    <ResumeCheckbox checked={phase3Resume} onChange={setPhase3Resume} />
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {firstSelectedPhase && selectedProjects.length > 0 && (
                    <Paper variant="outlined" className={classes.tableContainer}>
                        <Typography variant="subtitle2" style={{ padding: "12px 16px 0" }}>
                            Eligibility — requires latest <code>{firstSelectedPhase.datasetType}</code>
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Project</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Latest version_dttm</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedProjects.map((project) => {
                                    const entry = eligibility[project] || { state: ELIGIBILITY.CHECKING };
                                    const greyed = entry.state !== ELIGIBILITY.ELIGIBLE;
                                    return (
                                        <TableRow
                                            key={project}
                                            sx={greyed ? { opacity: 0.55 } : undefined}
                                        >
                                            <TableCell>{project}</TableCell>
                                            <TableCell>{eligibilityChip(entry.state)}</TableCell>
                                            <TableCell>{entry.versionDttm || "—"}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Paper>
                )}

                <div className={classes.buttonRow}>
                    <Button
                        variant="contained"
                        disabled={!canSubmit}
                        onClick={() => setConfirmModalOpen(true)}
                    >
                        {`Run ${phaseLabel} for ${eligibleProjects.length} project(s)`}
                    </Button>
                </div>

                {hasRunStatus && (
                    <Paper variant="outlined" className={classes.tableContainer}>
                        <Typography variant="subtitle2" style={{ padding: "12px 16px 0" }}>
                            Submission status
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Project</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Progress</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {eligibleProjects.map((project) => {
                                    const entry = runStatus[project];
                                    return (
                                        <TableRow key={project}>
                                            <TableCell>{project}</TableCell>
                                            <TableCell>{runStatusChip(entry)}</TableCell>
                                            <TableCell>
                                                <Link
                                                    to={progressViewerUrlFor(project)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View
                                                </Link>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Paper>
                )}
            </Form>
        </div>
    );
}
